import { ChatMessage, Conversation } from '@/@types/api/chat';

// Base WebSocket Event interface
export interface WebSocketEvent<T = unknown> {
  event: string;
  data: T;
}

export interface ChatMessageData {
  conversationId: string;
  hasMore: boolean;
  messages: Array<{
    id: string;
    conversationId: string;
    content: string;
    createdAt: string;
    isRead: boolean;
    receiverId: string;
    senderId: string;
    type: string;
    updatedAt: string;
  }>;
  total: number;
}

// Event Types
export enum WebSocketEventType {
  // Chat Events
  CHAT_MESSAGE = 'chat:message',
  CHAT_TYPING = 'chat:typing',
  CHAT_READ = 'chat:read',
  CHAT_HISTORY = 'chat:history',
  CHAT_FETCH_HISTORY = 'chat:fetch_history',
  CHAT_CONVERSATIONS = 'chat:conversations',

  // Auth Events
  AUTH = 'auth',
  AUTH_RESPONSE = 'auth:response',

  // Connection Events
  CONNECT = 'connect',
  DISCONNECT = 'disconnect',
  ERROR = 'error',
}

// Event Payloads
export interface ChatMessagePayload {
  conversationId: string;
  message: ChatMessage;
}

export interface ChatTypingPayload {
  userId: string;
  conversationId: string;
}

export interface ChatReadPayload {
  messageId: string;
  userId: string;
}

export interface FetchChatHistoryPayload {
  conversationId: string;
}

export interface ChatHistoryPayload {
  conversationId: string;
  hasMore: boolean;
  messages: ChatMessage[];
  total: number;
}

export interface ChatConversationsPayload {
  conversations: Conversation[];
}

export interface AuthPayload {
  token: string;
}

export interface AuthResponsePayload {
  success: boolean;
  error?: string;
}

export interface ErrorPayload {
  code: string;
  message: string;
}

// Map event types to their payloads
export interface WebSocketEventMap {
  [WebSocketEventType.CHAT_MESSAGE]: ChatMessagePayload;
  [WebSocketEventType.CHAT_TYPING]: ChatTypingPayload;
  [WebSocketEventType.CHAT_READ]: ChatReadPayload;
  [WebSocketEventType.CHAT_HISTORY]: ChatHistoryPayload;
  [WebSocketEventType.CHAT_FETCH_HISTORY]: FetchChatHistoryPayload;
  [WebSocketEventType.CHAT_CONVERSATIONS]: ChatConversationsPayload;
  [WebSocketEventType.AUTH]: AuthPayload;
  [WebSocketEventType.AUTH_RESPONSE]: AuthResponsePayload;
  [WebSocketEventType.ERROR]: ErrorPayload;
  [WebSocketEventType.CONNECT]: undefined;
  [WebSocketEventType.DISCONNECT]: undefined;
}

// Type-safe event emitter and listener types
export type WebSocketEventListener<T extends WebSocketEventType> = (
  payload: WebSocketEventMap[T],
) => void;

export type WebSocketEventEmitter<T extends WebSocketEventType> = (
  payload: WebSocketEventMap[T],
) => void;
